import { useEffect, useState } from 'react';
import { projectsApi } from '@/api/projects.api';
import { EmployeeSelect, ProjectSelect } from '@/components/selects';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormGrid } from '@/components/ui/FormGrid';
import { useEmployeeOptions } from '@/hooks/useEmployeeOptions';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

interface ResourceAllocation {
  id: number;
  projectId: number;
  employeeId: number;
  allocationPercent?: number;
  roleCode?: string;
}

export function ProjectsResourcesPage() {
  const { getLabel } = useEmployeeOptions();
  const [projectId, setProjectId] = useState('');
  const [resources, setResources] = useState<ResourceAllocation[]>([]);
  const [employeeId, setEmployeeId] = useState('');
  const [percent, setPercent] = useState('100');

  useEffect(() => {
    if (!projectId) {
      setResources([]);
      return;
    }
    projectsApi.listProjectResources(Number(projectId)).then(setResources).catch((e) => showApiError(e, 'Failed to load resources'));
  }, [projectId]);

  const allocate = async () => {
    if (!projectId || !employeeId.trim()) return;
    try {
      await projectsApi.allocateResource({
        projectId: Number(projectId),
        employeeId: Number(employeeId),
        allocationPercent: Number(percent) || 100,
      });
      notify.success('Resource allocated');
      setResources(await projectsApi.listProjectResources(Number(projectId)));
    } catch (e) {
      showApiError(e, 'Allocate failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Resource planning"
        description="Team allocation and capacity by project"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Projects', to: '/projects' }, { label: 'Resources' }]}
      />
      <ProjectSelect className="mb-4" value={projectId} onChange={setProjectId} />
      <FormGrid columns={3} className="card mb-4 p-4 items-end">
        <EmployeeSelect value={employeeId} onChange={setEmployeeId} />
        <Input label="Allocation %" value={percent} onChange={(e) => setPercent(e.target.value)} />
        <Button onClick={() => void allocate()} disabled={!projectId}>Allocate</Button>
      </FormGrid>
      <ul className="card divide-y">
        {resources.map((r) => (
          <li key={r.id} className="flex justify-between p-3 text-sm">
            <span>{getLabel(r.employeeId, true)}</span>
            <span>{r.allocationPercent ?? 100}% · {r.roleCode ?? 'MEMBER'}</span>
          </li>
        ))}
        {!resources.length && <li className="p-4 text-sm text-slate-500">No allocations for this project.</li>}
      </ul>
    </div>
  );
}
